import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2, BookOpen, ScrollText, Users, GitCompare } from "lucide-react";
import { sectionBadgeColor, sectionLabel } from "@/components/RuleComponents";
import { AsyncCombobox } from "@/components/AsyncCombobox";
import { useNav, VIEWS } from "@/lib/nav";

export function RulesPage() {
  const { navigate } = useNav();
  const [query, setQuery] = useState("");
  const [section, setSection] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api
        .listRules({ q: query, section, limit: 100 })
        .then((d) => {
          setData(d);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query, section]);

  const sectionFilters = [
    { value: "", label: "All", testKey: "all" },
    { value: "cardAbility", label: "Card", testKey: "card" },
    { value: "modelAbility", label: "Model", testKey: "model" },
    { value: "weaponAbility", label: "Weapon", testKey: "weapon" },
    { value: "feat", label: "Feat", testKey: "feat" },
    { value: "spell", label: "Spell", testKey: "spell" },
  ];

  const fetchCardOptions = useCallback(async (q) => {
    const r = await api.listCards({ q, limit: 30 });
    return r.items.map((c) => ({
      id: c.id,
      name: c.name,
      sub: [c.faction, c.type, `${c.rule_count} rule${c.rule_count === 1 ? "" : "s"}`]
        .filter(Boolean).join(" · "),
    }));
  }, []);

  const fetchRuleOptions = useCallback(async (q) => {
    const r = await api.listRules({ q, limit: 30 });
    return r.items.map((it) => ({
      id: it.id,
      name: it.name,
      sub: `${sectionLabel(it.section)} · on ${it.usage_count} card${
        it.usage_count === 1 ? "" : "s"
      }${it.duplicate_count > 1 ? ` · ${it.duplicate_count}× dup` : ""}`,
    }));
  }, []);

  return (
    <div className="grid gap-3" data-testid="rules-page">
      {/* Header: dropdowns + search + filters */}
      <Card>
        <CardContent className="p-4 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <AsyncCombobox
              placeholder="Pick a model card…"
              triggerLabel="Pick a model card…"
              icon={<Users className="w-4 h-4 text-cyan-400" />}
              fetcher={fetchCardOptions}
              onSelect={(it) => navigate(VIEWS.card(it.id))}
              testId="pick-model-combobox"
            />
            <AsyncCombobox
              placeholder="Pick a rule…"
              triggerLabel="Pick a rule…"
              icon={<ScrollText className="w-4 h-4 text-purple-400" />}
              fetcher={fetchRuleOptions}
              onSelect={(it) => navigate(VIEWS.rule(it.id))}
              testId="pick-rule-combobox"
            />
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search rule name or text… (e.g. ‘pathfinder’, ‘knocked down’)"
                className="pl-9"
                data-testid="rules-search-input"
              />
            </div>
            <div className="flex gap-1">
              {sectionFilters.map((f) => (
                <Button
                  key={f.value}
                  variant={section === f.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSection(f.value)}
                  data-testid={`filter-chip-${f.testKey}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            <span
              className="text-xs text-muted-foreground whitespace-nowrap"
              data-testid="rules-count"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin inline" />
              ) : (
                `${data.total.toLocaleString()} rules`
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(VIEWS.spellOverlap())}
              className="text-violet-300 border-violet-700/60"
              data-testid="open-spell-overlap-btn"
            >
              <GitCompare className="w-3.5 h-3.5 mr-1.5" /> Spell overlaps
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="space-y-2" data-testid="rules-list">
        {loading && data.items.length === 0 && (
          <>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </>
        )}
        {!loading && data.items.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              No matching rules.
            </CardContent>
          </Card>
        )}
        {data.items.map((rule) => (
          <RuleRow key={rule.id} rule={rule} onClick={() => navigate(VIEWS.rule(rule.id))} />
        ))}
      </div>
    </div>
  );
}

function RuleRow({ rule, onClick }) {
  return (
    <Card
      className="cursor-pointer transition hover:border-primary/50 hover:bg-card/80"
      onClick={onClick}
      data-testid={`rule-row-${rule.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold leading-none truncate">{rule.name}</h3>
              <Badge variant="outline" className={sectionBadgeColor(rule.section)}>
                {sectionLabel(rule.section)}
              </Badge>
              {rule.duplicate_count > 1 && (
                <Badge
                  variant="outline"
                  className="text-rose-300 border-rose-700/60 bg-rose-950/30"
                  title="Same rule exists as multiple ability IDs in the source data"
                  data-testid="duplicate-badge"
                >
                  {rule.duplicate_count}× duplicate
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">{rule.text}</p>
          </div>
          <div
            className="flex flex-col items-end gap-1 text-xs text-muted-foreground shrink-0"
            data-testid="cards-counter"
          >
            <span className="flex items-center gap-1 text-primary">
              <BookOpen className="w-3 h-3" />
              {rule.usage_count}
            </span>
            <span>cards</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
